import React from "react";
import { Avatar } from "@fluentui/react-components";
import HoverCard from "../../components/hoverCard/HoverCard";
import {
    formatMonthYear,
    getReshuffleStatusLabel,
    getReshuffleStatusColorClass,
    formatPeriodMonthYear,
} from "../../helpers/utils";
import styles from "./ReshuffleRequest.module.scss";
import { getAccountCurrency, displayDecimalFormatted } from "../../utils/systemSettings";

const setting = JSON.parse(localStorage.getItem("settings"));

const formatCurrency = (value) => {
    return `${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Number(value))}`;
};

const formatNumber = (value) => {
    return displayDecimalFormatted(setting, Number(value));
};

const DeltaValue = ({ value }) => {
    if (value > 0) return <span className={styles.deltaNegative}>{`+ ${formatCurrency(value)}`}</span>;
    if (value < 0) return <span className={styles.deltaPositive}>{`- ${formatCurrency(Math.abs(value))}`}</span>;
    return <span className={styles.deltaNeutral}>{formatCurrency(0)}</span>;
};

const UserHoverCell = ({ user }) => {
    if (!user) return <span></span>;
    return (
        <HoverCard
            HoverCardTrigger={
                <div className={styles.userCell}>
                    <Avatar
                        name={user.name}
                        image={{ src: user.image }}
                        color="colorful"
                        size={20}
                    />
                    <span>{user.name}</span>
                </div>
            }
            AvatarId={user.id}
            AvatarName={user.name}
            AvatarImage={user.image}
            AvatarDesignation={user.title}
            AvatarEmail={user.email}
            AvatarPhone={user.phoneNumber}
            AvatarIsExternal={user.isExternal}
        />
    );
};

const FinancialsCard = ({ title, data }) => {
    if (!data) return null;
    const fields = [
        { label: "BAC", key: "bac" },
        { label: "Budget", key: "budget" },
        { label: "ETC", key: "etc" },
        { label: "Actual", key: "actual" },
        { label: "Accrual", key: "accural" },
        { label: "Spent", key: "spent" },
        { label: "Invoiced", key: "invoiced" },
        { label: "CV", key: "cv" },
    ];
    return (
        <div className={styles.financialsCard}>
            <div className={styles.financialsCardTitle}>{title}</div>
            {fields.map(({ label, key }) => (
                <div key={key} className={styles.financialRow}>
                    <span>{label}</span>
                    <span>{formatCurrency(data[key])}</span>
                </div>
            ))}
        </div>
    );
};

/**
 * ReshuffleSummaryPreview
 * Accepts `preview` (ReshuffleSummaryDto) and renders the full summary layout.
 * Used in Step 3 of the create wizard and in the "Summary" view mode.
 */
const ReshuffleSummaryPreview = ({ preview }) => {
    if (!preview) return null;

    const { request, resourceBreakdown, projectFinancialsBefore, projectFinancialsAfter, totalCostDelta, isCostNeutral } = preview;

    const deltaCls = totalCostDelta > 0 ? "positive" : "negative";

    return (
        <div className={styles.summaryWrapper}>
            {/* ── Header ──────────────────────────────────────────────────────── */}
            {request && (
                <div className={styles.summaryHeader}>
                    <div className={styles.summaryCell}>
                        <p className={styles.summaryTitle}>
                            <UserHoverCell user={request.targetResource} />
                        </p>
                    </div>
                    <div className={styles.summaryCell}>
                        <p className={styles.summarySubtitle}>
                            {request.periodStartYear &&
                                `${formatPeriodMonthYear(request.periodStartMonth, request.periodStartYear, request.periodEndMonth, request.periodEndYear)}`}
                            {"  "}
                            <span className={styles.summaryStatusBadge}>
                                {request.requestedHours} hrs
                            </span>
                        </p>
                    </div>
                    <div className={styles.summaryCell}>
                        {request.status !== undefined && (
                            <span className={styles.summaryStatusBadge}>
                                {getReshuffleStatusLabel(request.status)}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* ── Cost Delta ──────────────────────────────────────────────────── */}
            <div className={styles.costDeltaSection}>
                <div className={`${styles.costDeltaCard} ${styles[deltaCls]}`}>
                    <div className={styles.costDeltaLabel}>Total Cost Delta</div>
                    <div className={`${styles.costDeltaValue} ${styles[deltaCls]}`}>
                        {totalCostDelta > 0 ? "+" : ""}{formatCurrency(totalCostDelta)}
                    </div>
                </div>
            </div>

            {/* ── Resource Breakdown ──────────────────────────────────────────── */}
            {resourceBreakdown?.length > 0 && (
                <>
                    <div className={styles.sectionTitle}>Request Breakdown</div>
                    <table className={styles.breakdownTable}>
                        <thead>
                            <tr>
                                <th>Resource</th>
                                <th>Role</th>
                                <th>Month</th>
                                <th>Current Hrs</th>
                                <th>Projected Hrs</th>
                                <th>Hrs Variance</th>
                                <th>Cost Rate</th>
                                <th>Current Cost</th>
                                <th>Projected Cost</th>
                                <th>Cost Variance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {resourceBreakdown.map((row, i) => {
                                const hrsDelta = (row.projectedForecastHours || 0) - (row.currentForecastHours || 0);
                                return (
                                    <tr key={i}>
                                        <td><UserHoverCell user={row.resource} /></td>
                                        <td>
                                            {row.role === "Target" || row.role === 0 ? (
                                                <span className={styles.roleBadgeTarget}>Target</span>
                                            ) : (
                                                <span className={styles.roleBadgeDonor}>Donor</span>
                                            )}
                                        </td>
                                        <td>{formatMonthYear(row.year, row.month)}</td>
                                        <td>{formatNumber(row.currentForecastHours)}</td>
                                        <td>{formatNumber(row.projectedForecastHours)}</td>
                                        <td>
                                            {hrsDelta > 0 ? (
                                                <span className={styles.deltaPositive}>+{formatNumber(hrsDelta)}</span>
                                            ) : hrsDelta < 0 ? (
                                                <span className={styles.deltaNegative}>{formatNumber(hrsDelta)}</span>
                                            ) : (
                                                <span className={styles.deltaNeutral}>0</span>
                                            )}
                                        </td>
                                        <td>{formatCurrency(row.hourlyCostRate)}</td>
                                        <td>{formatCurrency(row.currentForecastCost)}</td>
                                        <td>{formatCurrency(row.projectedForecastCost)}</td>
                                        <td><DeltaValue value={row.costDelta} /></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}

            {/* ── Project Financials Before / After ──────────────────────────── */}
            {/* {(projectFinancialsBefore || projectFinancialsAfter) && (
                <>
                    <div className={styles.sectionTitle}>Project Financials</div>
                    <div className={styles.financialsGrid}>
                        <FinancialsCard title="Before" data={projectFinancialsBefore} />
                        <FinancialsCard title="After" data={projectFinancialsAfter} />
                    </div>
                </>
            )} */}
        </div>
    );
};

export default ReshuffleSummaryPreview;
